import { useState, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { transactionsApi } from '@/api/client';
import type { Transaction } from '@/types';
import { format, parseISO, subDays } from 'date-fns';
import { ja } from 'date-fns/locale';
import clsx from 'clsx';

export function HistoryPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchTransactions = async () => {
    setIsLoading(true);
    const response = await transactionsApi.list({
      search,
      type: typeFilter as 'in' | 'out' | 'adjust' | undefined,
      start_date: startDate,
      end_date: endDate,
      page,
      limit: 50,
    });

    if (response.success && response.data) {
      setTransactions(response.data.items);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchTransactions();
  }, [search, typeFilter, startDate, endDate, page]);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'in': return '入庫';
      case 'out': return '出庫';
      case 'adjust': return '調整';
      default: return type;
    }
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'in': return 'badge-success';
      case 'out': return 'badge-info';
      case 'adjust': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">入出庫履歴</h1>
        <span className="text-sm text-gray-500">
          {total}件
        </span>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="商品名・ロット番号で検索..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input pl-10"
            />
          </div>
          
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="input lg:w-32"
          >
            <option value="">すべて</option>
            <option value="in">入庫</option>
            <option value="out">出庫</option>
            <option value="adjust">調整</option>
          </select>

          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="input"
            />
            <span className="text-gray-500">〜</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            履歴がありません
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>日時</th>
                  <th>種別</th>
                  <th>商品名</th>
                  <th>ロット番号</th>
                  <th className="text-right">数量</th>
                  <th className="text-right">変更後</th>
                  <th>担当者</th>
                  <th>メモ</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="whitespace-nowrap">
                      {format(parseISO(tx.created_at), 'MM/dd HH:mm', { locale: ja })}
                    </td>
                    <td>
                      <span className={getTypeBadgeClass(tx.type)}>
                        {getTypeLabel(tx.type)}
                      </span>
                    </td>
                    <td className="font-medium">{tx.product_name}</td>
                    <td className="font-mono text-sm">{tx.lot_number}</td>
                    <td className="text-right">
                      <span className={clsx(
                        'font-medium',
                        tx.type === 'in' ? 'text-green-600' :
                        tx.type === 'out' ? 'text-blue-600' : 'text-yellow-600'
                      )}>
                        {tx.type === 'in' ? '+' : tx.type === 'out' ? '-' : ''}
                        {Math.abs(tx.quantity)}
                      </span>
                      <span className="text-gray-500 ml-1">{tx.product_unit}</span>
                    </td>
                    <td className="text-right text-gray-600">
                      {tx.quantity_after}{tx.product_unit}
                    </td>
                    <td>{tx.user_name}</td>
                    <td className="max-w-[150px] truncate text-gray-500">
                      {tx.note || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary px-3 py-1 text-sm"
            >
              前へ
            </button>
            <span className="text-sm text-gray-600">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary px-3 py-1 text-sm"
            >
              次へ
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
